import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileCheck2, Download, Loader2, FileText, Package } from 'lucide-react';
import { tuvDocsAPI } from '@/lib/api';
import { toast } from 'sonner';

export default function TuvDocs() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zipLoading, setZipLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [activeContent, setActiveContent] = useState('');
  const [contentLoading, setContentLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await tuvDocsAPI.list();
        setDocs(r.data || []);
        if (r.data?.length) {
          selectDoc(r.data[0].id);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load TÜV docs');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectDoc = async (id) => {
    setActive(id);
    setContentLoading(true);
    try {
      const r = await tuvDocsAPI.get(id);
      setActiveContent(r.data.content || '');
    } catch (e) {
      toast.error('Failed to load document');
      setActiveContent('');
    } finally {
      setContentLoading(false);
    }
  };

  const downloadZip = async () => {
    setZipLoading(true);
    try {
      const res = await fetch(tuvDocsAPI.downloadZipUrl(), { credentials: 'include' });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename=([^;]+)/i);
      a.download = m ? m[1].trim() : 'Veridex_TUV_Pack.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('TÜV pack downloaded');
    } catch (e) {
      toast.error(e.message || 'Failed to download ZIP');
    } finally {
      setZipLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="tuv-docs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileCheck2 className="w-6 h-6 text-primary" /> TÜV Documentation Pack
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Methodology, audit guide, and pre-audit checklist ready to share with your TÜV assessor.
          </p>
        </div>
        <Button onClick={downloadZip} disabled={zipLoading} data-testid="download-zip-btn">
          {zipLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Download ZIP
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="grid lg:grid-cols-[260px_1fr] gap-6">
          <Card className="p-3" data-testid="tuv-doc-list">
            <div className="text-xs uppercase tracking-widest text-muted-foreground px-2 py-1">
              <Package className="w-3 h-3 inline mr-1" /> {docs.length} documents
            </div>
            <div className="space-y-1 mt-2">
              {docs.map((d) => (
                <button
                  key={d.id}
                  data-testid={`tuv-doc-${d.id}`}
                  onClick={() => selectDoc(d.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-start gap-2 ${
                    active === d.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                  }`}
                >
                  <FileText className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{d.title}</div>
                    <div className="text-[11px] text-muted-foreground font-mono truncate">{d.filename}</div>
                  </div>
                  <Badge variant="outline" className="text-[10px] font-mono">
                    {(d.size_bytes / 1024).toFixed(1)}kB
                  </Badge>
                </button>
              ))}
            </div>
          </Card>

          <Card className="p-6 overflow-hidden" data-testid="tuv-doc-viewer">
            {contentLoading ? (
              <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
              <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed max-h-[70vh] overflow-auto">
                {activeContent}
              </pre>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
